import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FbProvider, useFbClient } from '@featbit/react-client-sdk';
import { selectUser, selectSite } from '../store/authReducer';

const FEATBIT_SDK_KEY = process.env.REACT_APP_FEATBIT_SDK_KEY;
const FEATBIT_STREAMING_URI = process.env.REACT_APP_FEATBIT_STREAMING_URI;
const FEATBIT_EVENTS_URI = process.env.REACT_APP_FEATBIT_EVENTS_URI;

const FbUserSync = ({ user, children }) => {
  const fbClient = useFbClient();
  const prevUserRef = useRef(null);

  useEffect(() => {
    if (!fbClient || !user) return;
    const prev = prevUserRef.current;
    prevUserRef.current = user;
    if (!prev) return;
    if (prev.keyId !== user.keyId) {
      fbClient.identify(user);
      return;
    }
    const prevProps = prev.customizedProperties || [];
    const currProps = user.customizedProperties || [];
    const changed = currProps.some((cp) => {
      const pp = prevProps.find((p) => p.name === cp.name);
      return !pp || pp.value !== cp.value;
    });
    if (changed) {
      fbClient.identify(user);
    }
  }, [fbClient, user]);

  return children;
};

const FeatureFlagProvider = ({ children }) => {
  const user = useSelector(selectUser);
  const siteId = useSelector(selectSite);

  if (!FEATBIT_SDK_KEY || !FEATBIT_STREAMING_URI || !FEATBIT_EVENTS_URI) {
    return <>{children}</>;
  }

  if (!user) {
    return <>{children}</>;
  }

  const fbUser = {
    keyId: String(user.id),
    name: `${user.firstName} ${user.lastName}`,
    customizedProperties: [
      { name: 'role', value: user.role || '' },
      { name: 'siteId', value: String(siteId || '') },
      { name: 'email', value: user.email || '' },
      { name: 'operatorGroupId', value: String(user.operatorGroupId || '') },
    ],
  };

  const options = {
    sdkKey: FEATBIT_SDK_KEY,
    streamingUri: FEATBIT_STREAMING_URI,
    eventsUri: FEATBIT_EVENTS_URI,
    user: fbUser,
  };

  return (
    <FbProvider options={options}>
      <FbUserSync user={fbUser}>{children}</FbUserSync>
    </FbProvider>
  );
};

export default FeatureFlagProvider;
